import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Container, Row } from 'react-bootstrap';
import { route } from '../utils/apiRoutes';
import { normalizeGoalType, goalTypeToUrl } from '../utils/goalTypeUtils';
import { sortBySdgIndex } from '../utils/sortUtils';
import { getGoalDisplayTitle } from '../utils/goalDisplayTitle';
import SDGSpinner from './SDGSpinner';
import '../utils/sharedStyles.css';

/**
 * Catalog of all indicators (including incomplete / draft). Incomplete and draft
 * rows are listed grayed-out (no detail link). Used from home "View all indicators".
 */
const IndicatorsCatalogPage = () => {
  const { goalType: goalTypeParam } = useParams();
  const goalType = normalizeGoalType(goalTypeParam);
  const [goals, setGoals] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const url = route.indicatorsListWithIncomplete(goalType);
    const goalsUrl = route.goalsList();
    Promise.all([fetch(url), fetch(goalsUrl)])
      .then(([indRes, goalsRes]) => {
        if (cancelled) return;
        if (!indRes.ok || !goalsRes.ok) throw new Error('Failed to fetch data');
        return Promise.all([indRes.json(), goalsRes.json()]);
      })
      .then(([indData, goalsData]) => {
        if (cancelled) return;
        setIndicators(Array.isArray(indData) ? indData : []);
        setGoals(Array.isArray(goalsData) ? goalsData : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load indicators');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [goalType]);

  const goalsById = goals.reduce((acc, g) => ({ ...acc, [g.id]: g }), {});

  const byGoal = useMemo(() => {
    const grouped = indicators.reduce((acc, ind) => {
      const gid = ind.goal_id || 0;
      if (!acc[gid]) acc[gid] = [];
      acc[gid].push(ind);
      return acc;
    }, {});
    Object.keys(grouped).forEach((gid) => {
      grouped[gid] = sortBySdgIndex(grouped[gid]);
    });
    return grouped;
  }, [indicators]);

  const orderedGoalIds = useMemo(() => {
    const sortedGoals = sortBySdgIndex(goals);
    const goalOrder = sortedGoals.map((g) => g.id).filter(Boolean);
    const orphanIds = Object.keys(byGoal)
      .map(Number)
      .filter((id) => !goalOrder.includes(id));
    return [...goalOrder, ...orphanIds];
  }, [goals, byGoal]);

  const formatLabel = (ind) => {
    const code = (ind.indicator_code || '').trim();
    const title = (ind.title || '').trim();
    if (code && title) return `${code} · ${title}`;
    return code || title || `Indicator #${ind.id}`;
  };

  if (loading) {
    return (
      <Container className="py-5">
        <SDGSpinner text="Loading all indicators..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <p style={{ color: '#721c24' }}>{error}</p>
        <p className="text-muted">Use the site navigation to return to the goal overview.</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <h2>{goalType === 'eu' ? 'EU' : 'UN'} SDG Indicators – Full list</h2>
        <p className="text-muted">
          Indicators with data and configuration are clickable. Draft or incomplete
          indicators are listed in grey and do not have a detail page yet.
        </p>
      </Row>
      {orderedGoalIds.map((goalId) => {
        const list = byGoal[goalId] || [];
        if (list.length === 0) return null;
        const g = goalsById[goalId];
        const goalTitle = goalId
          ? getGoalDisplayTitle({
              id: g?.id,
              title: g?.title,
              title_render: g?.title,
              acf: { index: g?.index, name: g?.title },
            })
          : 'Unassigned';
        return (
          <div key={goalId} className="mb-4">
            <h5 className="section-title" style={{ marginBottom: '12px' }}>{goalTitle}</h5>
            <ul className="list-unstyled" style={{ paddingLeft: '0' }}>
              {list.map((ind) => {
                const complete = ind.setup_complete === true;
                const isDraft = ind.post_status === 'draft';
                const clickable = complete && !isDraft;
                const detailUrl = `/${goalTypeToUrl(goalType)}/goal/${goalId}/indicators/${ind.id}`;
                const label = formatLabel(ind);
                const mutedNote = isDraft
                  ? '(Draft)'
                  : '(Data not yet available)';
                return (
                  <li key={ind.id} style={{ marginBottom: '8px' }}>
                    {clickable ? (
                      <Link to={detailUrl} style={{ textDecoration: 'none' }}>
                        {label}
                      </Link>
                    ) : (
                      <span style={{ color: '#6c757d' }}>
                        {label}
                        <span className="ms-2 small text-muted">{mutedNote}</span>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
      {indicators.length === 0 && (
        <p className="text-muted">No indicators found.</p>
      )}
    </Container>
  );
};

export default IndicatorsCatalogPage;
